// ./src/hooks/useAccountPreference.ts
//
// The account's event defaults (ROADMAP A2b #53) — read once, saved as a partial patch.
//
// ⚠⚠ NOT A COLLECTION HOOK. Every other entity hook here holds a list, a page and a search; this
// holds ONE record that always exists conceptually (an account that has never saved has an empty
// one). Modelling it as a list of length ≤ 1 would push "which one is mine" into every caller.
import { useCallback, useEffect, useRef, useState } from 'react';
import { OpResult, OP_RESULT_CODES } from '@sdflc/api-helpers';

import { AccountPreference, AccountPreferenceInput, AccountPreferenceService } from '@/utils/api';

export interface UseAccountPreferenceOptions {
  /** False keeps the hook idle (a crew session has no account preferences to read). */
  enabled?: boolean;
  autoLoad?: boolean;
}

export interface UseAccountPreferenceReturn {
  preference: AccountPreference | null;
  loading: boolean;
  isLoaded: boolean;
  isSaving: boolean;
  /**
   * The last READ failed (ROADMAP #161 review). ⚠ `isLoaded` is true after a failed read too, with no preference — so
   * "loaded, no default venue" and "could not read it" look the same without this; a screen that offers to SET the
   * default must not treat the second as the first (it would show an empty picker over a default that exists).
   */
  loadFailed: boolean;
  lastError: OpResult | null;
  clearError: () => void;
  /** Drop the loaded row — called on sign-out. */
  reset: () => void;
  reload: () => Promise<void>;
  /**
   * Save a PATCH. Resolves THIS save's OpResult — read the refusal from it, never from `lastError` after the await: the
   * caller's `lastError` is the value from the render before the click (#161 review: two screens showed a stale or
   * generic message for every refusal that way).
   */
  save: (params: AccountPreferenceInput) => Promise<OpResult>;
}

export const useAccountPreference = (options: UseAccountPreferenceOptions = {}): UseAccountPreferenceReturn => {
  const { enabled = true, autoLoad = true } = options;

  const [preference, setPreference] = useState<AccountPreference | null>(null);
  const [loading, setLoading] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [lastError, setLastError] = useState<OpResult | null>(null);

  // ⚠ A LOAD IN FLIGHT MUST NOT OVERWRITE A NEWER ONE. The provider is mounted app-wide and the page
  // can reload while the first fetch is still out.
  const requestRef = useRef(0);

  const load = useCallback(async (): Promise<void> => {
    if (!enabled) {
      return;
    }

    const request = requestRef.current + 1;

    requestRef.current = request;
    setLoading(true);

    const result = await AccountPreferenceService.get();

    if (requestRef.current !== request) {
      return;
    }

    if (result.code === OP_RESULT_CODES.OK) {
      setPreference(result.getDataFirst() ?? null);
      setLastError(null);
      setLoadFailed(false);
    } else {
      setLastError(result);
      setLoadFailed(true);
    }

    setLoading(false);
    setIsLoaded(true);
  }, [enabled]);

  useEffect(() => {
    if (enabled && autoLoad) {
      load();
    }
  }, [enabled, autoLoad, load]);

  const save = useCallback(async (params: AccountPreferenceInput): Promise<OpResult> => {
    setIsSaving(true);
    setLastError(null);

    const result = await AccountPreferenceService.save(params);

    setIsSaving(false);

    if (result.code !== OP_RESULT_CODES.OK) {
      setLastError(result);

      return result;
    }

    // ⚠ PATCHED FROM THE RETURNED RECORD, not re-fetched: the server is the one that normalised the
    // values and resolved a removed default venue away, so its answer is the truth about what is
    // now stored. A re-fetch would show the same thing one round-trip later.
    const saved = result.getDataFirst() ?? null;

    setPreference(saved);
    setIsLoaded(true);
    setLoadFailed(false);

    return result;
  }, []);

  const clearError = useCallback(() => setLastError(null), []);

  /**
   * ⚠ THE PROVIDER OUTLIVES THE SESSION. It sits above the routes, so signing out and signing in as
   * somebody else does not unmount it — without this, the second account would start every event
   * from the first account's currency and venue, with no indication of where the values came from.
   * ⚠ BUMPING THE REQUEST COUNTER IS PART OF THE RESET: a fetch already in flight for the previous
   * account would otherwise land after it and put the row straight back.
   */
  const reset = useCallback(() => {
    requestRef.current += 1;
    setPreference(null);
    setIsLoaded(false);
    setLoadFailed(false);
    setLoading(false);
    setLastError(null);
  }, []);

  return { preference, loading, isLoaded, isSaving, loadFailed, lastError, clearError, reset, reload: load, save };
};
